import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, errorMessage } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import AiScoreBadge from "../../components/AiScoreBadge";
import StatusBadge from "../../components/StatusBadge";
import DocumentsPanel from "../../components/DocumentsPanel";
import PreApprovalReadOnly from "../../components/PreApprovalReadOnly";
import { fmtCurrency, fmtDate, fmtDateTime, LOAN_TYPE_LABELS } from "../../lib/constants";
import { ArrowLeft, Send, Mail } from "lucide-react";
import { toast } from "sonner";

export default function SponsorApplicationDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [app, setApp] = useState(null);
  const [resending, setResending] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/sponsor/applications/${id}`);
      setApp(data);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const requestDocs = async () => {
    try {
      await api.post(`/sponsor/applications/${id}/request-docs`);
      toast.success("Documents requested");
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const resendInvite = async () => {
    setResending(true);
    try {
      const { data } = await api.post(`/sponsor/applications/${id}/resend-invite`);
      if (data.email_sent) {
        toast.success(`Invite resent to ${data.recipient}`);
      } else {
        toast.warning(`Logged resend — email send failed: ${data.email_error || "sandbox/verification"}`);
      }
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setResending(false);
    }
  };

  if (!app) return <div className="text-sm text-slate-400">Loading…</div>;

  const ai = app.ai_score || { total: 0, breakdown: {} };
  const brk = ai.breakdown || {};

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <button onClick={() => nav(-1)} className="mb-2 inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200" data-testid="detail-back-btn">
            <ArrowLeft className="h-3 w-3" /> Back
          </button>
          <h1 className="text-3xl font-semibold tracking-tight">{app.borrower_name}</h1>
          <div className="mt-1 text-sm text-slate-400">{app.property_address}</div>
          {app.invite_resend_count > 0 && (
            <div className="mt-2 text-[11px] text-slate-500" data-testid="detail-resend-meta">
              Invite resent {app.invite_resend_count} time{app.invite_resend_count > 1 ? "s" : ""}
              {app.last_invite_resent_at && ` · last on ${fmtDateTime(app.last_invite_resent_at)}`}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <AiScoreBadge score={ai} testId="detail-ai-score" />
          <StatusBadge status={app.status} testId="detail-status" />
          <Button
            onClick={resendInvite}
            disabled={resending}
            variant="outline"
            className="border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800 disabled:opacity-60"
            data-testid="detail-resend-invite-btn"
            title="Send the borrower a fresh magic-link email"
          >
            <Mail className="mr-2 h-4 w-4" />
            {resending ? "Resending…" : "Resend invite"}
          </Button>
          <Button onClick={requestDocs} className="bg-emerald-600 text-white hover:bg-emerald-500" data-testid="detail-request-docs-btn">
            <Send className="mr-2 h-4 w-4" /> Request Documents
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="border-slate-800 bg-slate-900 p-6 lg:col-span-2">
          <div className="mb-4 text-sm font-semibold">Application Details</div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Detail label="Email" value={app.borrower_email} />
            <Detail label="Phone" value={app.borrower_phone || "—"} />
            <Detail label="Loan Type" value={LOAN_TYPE_LABELS[app.loan_type] || app.loan_type} />
            <Detail label="Property Type" value={app.property_type} />
            <Detail label="Purchase Price" value={fmtCurrency(app.purchase_price)} />
            <Detail label="Expected Loan" value={fmtCurrency(app.expected_loan_amount)} />
            <Detail label="Rehab Budget" value={fmtCurrency(app.estimated_rehab_budget)} />
            <Detail label="ARV" value={fmtCurrency(app.arv)} />
            <Detail label="Credit Score" value={app.credit_score} />
            <Detail label="Capital Available" value={fmtCurrency(app.capital_available)} />
            <Detail label="# Projects" value={app.num_projects} />
            <Detail label="LLC" value={app.property_in_llc ? (app.llc_name || "Yes") : "No"} />
            <Detail label="Beds / Baths" value={`${app.beds} / ${app.baths}`} />
            <Detail label="Sq Ft" value={app.living_sqft || "—"} />
            <Detail label="Submitted by" value={app.submitted_by === "sponsor" ? "Broker" : "Borrower"} />
            <Detail label="Created" value={fmtDate(app.created_at)} />
          </div>
        </Card>

        <Card className="border-slate-800 bg-slate-900 p-6">
          <div className="mb-4 text-sm font-semibold">AI Pre-Qualification</div>
          <div className="mb-6">
            <div className="text-5xl font-semibold tabular-nums">{ai.total}</div>
            <div className="mt-1 text-xs uppercase tracking-widest text-slate-500">{ai.category}</div>
          </div>
          <ScoreRow label="Credit Score" value={brk.credit_score || 0} max={30} />
          <ScoreRow label="Experience" value={brk.experience || 0} max={25} />
          <ScoreRow label="Deal Numbers" value={brk.deal_numbers || 0} max={25} />
          <ScoreRow label="Capital & LLC" value={brk.capital_llc || 0} max={10} />
          <ScoreRow label="Property Info" value={brk.property_info || 0} max={10} />
        </Card>
      </div>

      <Card className="border-slate-800 bg-slate-900 p-6" data-testid="detail-status-history">
        <div className="mb-4 text-sm font-semibold">Status History</div>
        <div className="space-y-3">
          {(app.status_history || []).map((h, i) => (
            <div key={`${h.status}-${h.at || i}`} className="flex items-start gap-4 border-l-2 border-emerald-500/30 pl-4">
              <div>
                <StatusBadge status={h.status} />
                <div className="mt-1 text-xs text-slate-500">{fmtDateTime(h.at)}</div>
                {h.note && <div className="mt-1 text-sm text-slate-300">{h.note}</div>}
              </div>
            </div>
          ))}
        </div>
      </Card>

      <DocumentsPanel
        applicationId={id}
        canUpload={true}
        title="Documents"
        subtitle="Upload supporting docs for this application. Docs uploaded by the borrower will also appear here."
      />

      <PreApprovalReadOnly applicationId={id} />

      {(app.requested_docs || []).length > 0 && (
        <Card className="border-slate-800 bg-slate-900 p-6" data-testid="detail-requested-docs">
          <div className="mb-3 text-sm font-semibold">Requested from borrower</div>
          <div className="text-xs text-slate-500 mb-4">
            These docs were flagged for the borrower in their invite email.
          </div>
          <ul className="space-y-2">
            {app.requested_docs.map((r, i) => (
              <li key={`${r.doc_type}-${r.label || i}`} className="flex items-center gap-3 rounded-md border border-slate-800 bg-slate-950 p-3 text-sm">
                <span className="text-emerald-400">·</span>
                <div>
                  <div className="font-medium text-slate-100">
                    {{
                      executed_contract: "Executed Contract",
                      drivers_license: "Driver's License / ID",
                      credit_report: "Credit Report",
                      bank_statement: "Bank Statements",
                      other: "Other",
                    }[r.doc_type] || r.doc_type}
                  </div>
                  {r.label && <div className="text-xs text-slate-400">{r.label}</div>}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-1 text-sm text-slate-100">{value ?? "—"}</div>
    </div>
  );
}

function ScoreRow({ label, value, max }) {
  const pct = Math.min(100, (value / max) * 100);
  return (
    <div className="mb-3">
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="text-slate-300">{label}</span>
        <span className="tabular-nums text-slate-100">{value}/{max}</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-slate-800">
        <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
